/**
 * Farben umrechnen und lesbar halten (Roadmap D-02, Leitlinie 7).
 *
 * Gespeichert wird immer ein OKLCH-String (`'oklch(0.57 0.21 213)'`), die
 * Dunkelvariante leitet `styles/tailwind.css` daraus ab. Der freie Farbwaehler
 * des Browsers liefert aber Hex - diese Datei rechnet hin und zurueck und
 * zieht jede frei gewaehlte Farbe in einen Helligkeitsbereich, der auf dem
 * dunklen wie auf dem hellen Hintergrund erkennbar bleibt. Eine fast schwarze
 * oder fast weisse Wunschfarbe wird so zu ihrem kraeftigen Gegenstueck mit
 * demselben Farbton.
 *
 * Rein und ohne DOM. Formeln: Bjoern Ottosson, "A perceptual color space for
 * image processing" (OKLab), sRGB nach IEC 61966-2-1.
 */

export interface Oklch {
  /** Helligkeit 0..1 */
  l: number;
  /** Buntheit, praktisch 0..0.37 */
  c: number;
  /** Farbton in Grad 0..360 */
  h: number;
}

/** Helligkeitsbereich, in dem eine Farbe auf beiden Hintergruenden traegt. */
export const LESBAR_L = { min: 0.52, max: 0.64 } as const;
/** Mehr Buntheit liegt ausserhalb des Bildschirmfarbraums. */
export const MAX_C = 0.21;

export function formatOklch({ l, c, h }: Oklch): string {
  const r = (n: number, d: number) => Math.round(n * 10 ** d) / 10 ** d;
  return `oklch(${r(l, 3)} ${r(c, 3)} ${r(h, 1)})`;
}

/** Liest `oklch(L C H)`; `undefined` bei allem anderen. */
export function parseOklch(value: string): Oklch | undefined {
  const m = value.trim().match(/^oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)(?:deg)?\s*(?:\/\s*[\d.%]+\s*)?\)$/i);
  if (!m) return undefined;
  const l = +m[1]! / (m[2] ? 100 : 1);
  return { l, c: +m[3]!, h: +m[4]! % 360 };
}

const toLinear = (u: number) => (u <= 0.04045 ? u / 12.92 : ((u + 0.055) / 1.055) ** 2.4);
const fromLinear = (u: number) => (u <= 0.0031308 ? 12.92 * u : 1.055 * u ** (1 / 2.4) - 0.055);

export function hexToOklch(hex: string): Oklch | undefined {
  const m = hex.trim().match(/^#?([0-9a-f]{6})$/i);
  if (!m) return undefined;
  const n = parseInt(m[1]!, 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => toLinear(v / 255)) as [number, number, number];

  const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;

  const c = Math.sqrt(A * A + B * B);
  const h = c < 1e-4 ? 0 : ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360;
  return { l: L, c, h };
}

/** Fuer `<input type="color">`: der naechstliegende Hexwert (ausserhalb sRGB abgeschnitten). */
export function oklchToHex({ l, c, h }: Oklch): string {
  const rad = (h * Math.PI) / 180;
  const A = c * Math.cos(rad);
  const B = c * Math.sin(rad);

  const l_ = (l + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m_ = (l - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s_ = (l - 0.0894841775 * A - 1.291485548 * B) ** 3;

  const rgb = [
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  ].map((v) => Math.round(Math.min(1, Math.max(0, fromLinear(v))) * 255));

  return `#${rgb.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Zieht eine Farbe in den lesbaren Bereich - Farbton bleibt, Helligkeit und
 * Buntheit werden begrenzt. Graue Wuensche bleiben grau.
 */
export function lesbar(farbe: Oklch): Oklch {
  return {
    l: Math.min(LESBAR_L.max, Math.max(LESBAR_L.min, farbe.l)),
    c: Math.min(MAX_C, farbe.c),
    h: farbe.h,
  };
}

/** Hex aus dem Farbwaehler -> lesbarer, speicherbarer OKLCH-String. */
export function eigeneFarbe(hex: string): string | undefined {
  const o = hexToOklch(hex);
  return o ? formatOklch(lesbar(o)) : undefined;
}

/** Gespeicherter Wert -> Hex fuer den Farbwaehler; Unlesbares wird Grau. */
export function alsHex(value: string | null | undefined): string {
  const o = value ? parseOklch(value) : undefined;
  return o ? oklchToHex(o) : '#808080';
}

/** Warum `lesbar` eine Wunschfarbe veraendern wuerde; `null`, wenn sie so bleiben kann. */
export function anpassungGrund(hex: string): 'zu hell' | 'zu dunkel' | 'zu bunt' | null {
  const o = hexToOklch(hex);
  if (!o) return null;
  if (o.l > LESBAR_L.max + 0.005) return 'zu hell';
  if (o.l < LESBAR_L.min - 0.005) return 'zu dunkel';
  if (o.c > MAX_C + 0.005) return 'zu bunt';
  return null;
}

/**
 * Abstand zweier gespeicherter Farben im OKLab-Raum (Helligkeit, a, b).
 * Unter ca. 0,03 sind sie auf einen Blick kaum zu unterscheiden.
 */
export function farbAbstand(a: string, b: string): number {
  const x = parseOklch(a), y = parseOklch(b);
  if (!x || !y) return Infinity;
  const ab = (o: Oklch) => [o.c * Math.cos((o.h * Math.PI) / 180), o.c * Math.sin((o.h * Math.PI) / 180)] as const;
  const [a1, b1] = ab(x), [a2, b2] = ab(y);
  return Math.hypot(x.l - y.l, a1 - a2, b1 - b2);
}

export const AEHNLICH_AB = 0.03;

/** Der erste Eintrag der Liste, dessen Farbe der gewaehlten zum Verwechseln aehnelt. */
export function aehnlichZu<T extends { color: string }>(farbe: string, andere: readonly T[]): T | undefined {
  return andere.find((x) => farbAbstand(farbe, x.color) < AEHNLICH_AB);
}
